"use client";

import { createContext, useContext, type ReactNode } from "react";
import type { OrgContextValue } from "@/lib/orgs/types";

const OrgContext = createContext<OrgContextValue | null>(null);

export function OrgProvider({
  value,
  children,
}: {
  value: OrgContextValue;
  children: ReactNode;
}) {
  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

/**
 * Active org for the current `/o/[orgSlug]/...` subtree. Throws when
 * called outside an OrgProvider so we fail loudly during integration
 * (e.g. a client component that forgot it lives under the layout).
 */
export function useOrg(): OrgContextValue {
  const v = useContext(OrgContext);
  if (!v) throw new Error("useOrg must be used inside OrgProvider");
  return v;
}

/**
 * Like useOrg() but returns null when there is no OrgProvider in
 * scope. Use only in components that can render outside the
 * `/o/[orgSlug]/…` subtree (e.g. the global Header).
 */
export function useOrgOptional(): OrgContextValue | null {
  return useContext(OrgContext);
}
